import React, { Suspense, useState, useEffect } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import { Images } from "./../assets/common";
import { Typography } from "@mui/material";
import Signup from "./Signup";
import LoginTestUser from "./LoginTestUser/LoginTestUser";
import WaitList from "./WaitList";
import AppContainer from "./AppContainer/AppContainer";
import ServerDown from "./ServerDown";
import AddTokens from "./AddTokens";
import TermsAndCondition from "./TermsAndCondition";
import PrivacyPolicy from "./PrivacyPolicy";
import AdminPortal from "./Admin/AdminPortal"; // Make sure this file exists
import VideoPlayer from "./VideoPlayer/VideoPlayer";
import DebugRoute from "./DebugRoute";

import {
  getUserDetail,
  isLoggedInUser,
  isWhiteListed,
} from "../constants/function";
import cookieStorage from "../services/cookie-storage";
import LiveVideoStreaming from "./Streaming";
import AddUsersToStream from './AddUsersToStream/AddUsersToStream';

function isAdminUser() {
  const user = getUserDetail();
  // Check if user has admin role
  return user && user.roles && user.roles.includes('admin');
}

function PrivateRoute({ component: Component, path, appProps, ...rest }) {
  const unauthorisedUrl = ["/", "Signup"];
  const userData = getUserDetail();
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  
  // console.log('🚪 PrivateRoute triggered for path:', rest.path);
  
  // If user is logged in but no userData, fetch it from API
  useEffect(() => {
    // console.log('🔍 useEffect triggered:', {
    //   isLoggedIn: isLoggedInUser(),
    //   isWhiteListed: isWhiteListed(),
    //   hasUserData: !!userData,
    //   isLoadingUserData
    // });
    
    if (isLoggedInUser() && isWhiteListed() && !userData && !isLoadingUserData) {
      // console.log('🔄 User is logged in but no localStorage data - fetching from API');
      setIsLoadingUserData(true);
      
      // Import ApiService dynamically to avoid circular imports
      import('../services/api-service').then(({ default: ApiService }) => {
        ApiService.getToken().then(() => {
          return ApiService.USER_PROFILE();
        }).then(response => {
          if (response && response.result) {
            // console.log('✅ Successfully fetched user data from API');
            localStorage.setItem('userData', JSON.stringify({ userData: response.result }));
            window.location.reload(); // Reload to use the new data
          }
        }).catch(error => {
          // console.error('❌ Failed to fetch user data:', error);
          // If API call fails, user probably needs to login again
          cookieStorage.deleteCookie('Authorization');
          localStorage.clear();
          window.location.href = '/';
        }).finally(() => {
          setIsLoadingUserData(false);
        });
      });
    }
  }, [userData, isLoadingUserData]);
  
  const routeDebugInfo = {
    path: rest.path,
    isLoggedIn: isLoggedInUser(),
    isWhiteListed: isWhiteListed(),
    userDataExists: !!userData,
    hasUserId: !!userData?._id,
    hasBirthYear: !!userData?.birthYear,
    birthYear: userData?.birthYear,
    authCookie: !!cookieStorage.getCookie("Authorization"),
    localStorageExists: !!localStorage.getItem("userData")
  };
  
  // console.log('🔍 PrivateRoute check:', routeDebugInfo);

  return (
    <Route
      {...rest}
      render={(props) => {
        if (
          isLoggedInUser() &&
          unauthorisedUrl.includes(props.location.pathname) &&
          isWhiteListed()
        ) {
          return (
            <Redirect
              to={{
                pathname: `/app/home`,
              }}
            />
          );
        } else if (
          isLoggedInUser() &&
          isWhiteListed() &&
          userData &&
          userData._id && // Make sure we have valid user data
          !userData["birthYear"]
        ) {
          // console.log('🚨 BIRTHYEAR CHECK TRIGGERED - User missing birthYear, redirecting to signup:', {
          //   userId: userData._id,
          //   firstName: userData.firstName,
          //   birthYear: userData.birthYear,
          //   hasBirthYear: !!userData.birthYear,
          //   currentPath: props.location.pathname
          // });
          
          // If user is on signup page, allow them to complete profile
          if (props.location.pathname === "/" || props.location.pathname === "/signup") {
            return <Signup {...props} />;
          }
          
          // Otherwise redirect to signup to complete profile
          return (
            <Redirect
              to={{
                pathname: "/signup",
                state: { needsBirthYear: true }
              }}
            />
          );
        } else if (isLoggedInUser() && isWhiteListed()) {
          // Show loading while fetching user data
          if (isLoadingUserData) {
            return (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                color: 'white',
                backgroundColor: '#0A0A0A'
              }}>
                Loading user data...
              </div>
            );
          }
          return <Component {...props} {...appProps} />;
        } else if (isLoggedInUser() && isWhiteListed() && !userData) {
          // User is authenticated but missing localStorage data - redirect to login to refresh
          // console.log('🔄 Authenticated user missing localStorage data - redirecting to login');
          cookieStorage.deleteCookie('Authorization');
          localStorage.clear();
          return (
            <Redirect
              to={{
                pathname: "/",
              }}
            />
          );
        } else if (isLoggedInUser() && !isWhiteListed()) {
          return (
            <Redirect
              to={{
                pathname: "/WaitList",
              }}
            />
          );
        } else {
          return (
            <Redirect
              to={{
                pathname: "/",
              }}
            />
          );
        }
      }}
    />
  );
}

function RouterOutlet(props) {
  return (
    <Switch
      fallback={
        <div style={{ position: "relative", height: "100vh" }}>
          <img style={{ width: "100%" }} src={Images.splash} alt="SIKI" />
          <Typography
            sx={{
              fontFamily: '"Anton", sans-serif',
              fontSize: "45px",
              position: "absolute",
              top: "0",
              bottom: "0",
              left: "0",
              right: "0",
              margin: "auto",
              width: "100px",
              height: "100px",
            }}
          >
            SIKI
          </Typography>
        </div>
      }
    >
        <Route
          path="/admin"
          render={(props) =>
            isLoggedInUser() && isAdminUser() ? (
              <AdminPortal {...props} />
            ) : (
              <Redirect to="/" />
            )
          }
        />
      <Route exact path="/" render={(props) => {
        const userData = getUserDetail();
        // console.log('Root route - isLoggedInUser:', isLoggedInUser());
        // console.log('Root route - isWhiteListed:', isWhiteListed());
        // console.log('Root route - userData:', userData);
        // console.log('Root route - hasBirthYear:', userData && userData["birthYear"]);
        
        // Prevent redirect loop by checking if we're already redirecting
        if (props.location.state && props.location.state.redirecting) {
          // console.log('Preventing redirect loop, showing Signup');
          return <Signup {...props} />;
        }
        
        if (isLoggedInUser() && isWhiteListed()) {
          // If no userData in localStorage, clear session and force fresh login
          if (!userData) {
            // console.log('🔄 No userData found, clearing session for fresh login');
            cookieStorage.deleteCookie('Authorization');
            localStorage.clear();
            return <Signup {...props} />;
          }
          
          // Check if user has birthYear, if not, show signup to complete profile
          if (!userData["birthYear"]) {
            // console.log('🚨 User missing birthYear, showing signup to complete profile');
            return <Signup {...props} />;
          }
          
          return <Redirect to={{ pathname: "/app/home", state: { redirecting: true } }} />;
        }
        return <Signup {...props} />;
      }} />
      <Route exact path="/WaitList"
        render={(props) => <WaitList {...props} />}
      />
      <Route exact path="/TermsAndCondition"
        render={(props) => <TermsAndCondition {...props} />}
      />
      <Route exact path="/PrivacyPolicy"
        render={(props) => <PrivacyPolicy {...props} />}
      />
      <Route path="/maintenance" component={ServerDown} />
      <Route path="/signup" component={Signup} />
      <Route path="/viaduct/:userNum" component={LoginTestUser} />
      <Route path="/addUsers/:userCount/toStream/:streamId" component={AddUsersToStream} />
      <Route path="/LiveVideoStreaming" component={LiveVideoStreaming} />
      <Route path="/video/:videoId" component={VideoPlayer} />
      <Route path="/debug-route" component={DebugRoute} />

      <Route exact path="/home" render={(props) => {
        // console.log('/home route hit - redirecting to /app/home');
        return <Redirect to="/app/home" />;
      }} />
      
      <PrivateRoute path="/app" component={AppContainer} />
      <PrivateRoute exact path="/add-siki-token" component={AddTokens} />
      
      {/* Fallback route - redirect to signup if no other route matches */}
      <Route render={(props) => {
        // console.log('Fallback route hit for path:', props.location.pathname);
        // Prevent infinite redirects by checking if we're already at root
        if (props.location.pathname === '/') {
          return <Signup {...props} />;
        }
        return <Redirect to="/" />;
      }} />
    </Switch>
  );
}

export default RouterOutlet;
